import React, {useState} from 'react';
import './CreateClientsPage.css';
import {ClientCreationDto, ClientCreationResponseDto, GrantTypes, Tenant} from '../../types';
import {useSwitchContext} from "@/components/button/SwitchButtonContext";
import {validateField} from "../../validations/FieldValidations";
import ClientCredentialsContent from "@/pages/clients/ClientCredentialsPage";
import {callGovernanceApi} from "@/utils/ProtectedApiCall";
import {StatusMessage} from "is24-corecss";
import Modal from "@/pages/Modals/Modal";

const initialFormState: ClientCreationDto = {
  clientId: '',
  description: '',
  owner: '',
  publicClient: false,
  thirdPartyClient: false,
  scopes: '',
  tokenTTL: '60',
  grantTypes: GrantTypes.CLIENT_CREDENTIALS,
  redirectUris: '',
  tenant: Tenant.DE,
};

export const ClientCreationContent: React.FC = () => {
  const [formState, setFormState] = useState<ClientCreationDto>(initialFormState);
  const [clientCredentials, setClientCredentials] = useState<ClientCreationResponseDto | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const {selectedTenant} = useSwitchContext();

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {name, value, type, checked} = e.target as HTMLInputElement;
    const error = validateField(name, value);

    const newValue = type === 'checkbox' ? checked : value;
    setFormState(prevState => ({
      ...prevState,
      [name]: newValue,
    }));

    setFieldErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error || '',
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Check for any validation errors
    const hasErrors = Object.values(fieldErrors).some((error) => error);
    if (hasErrors) {
      return;
    }
    setLoading(true);

    // Convert grantTypes into an array before sending to api call.
    const grantTypesArray = [formState.grantTypes];
    formState.tenant = selectedTenant
    const requestData = {
      ...formState,
      grantTypes: grantTypesArray
    };

    callGovernanceApi('/api/client', 'POST', requestData)
      .then((response) => {
          setClientCredentials(response);
          setFormSubmitted(true);
        }
      )
      .catch((error) => {
        setErrorMessage(error.message);
        setShowModal(true);
      })
      .finally(() => setLoading(false));
  }

  return (
    <div>
      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <StatusMessage headline="Error" statusType='error' message={errorMessage} statusStyle="toast" />
        </Modal>
      )}
      {!formSubmitted && (
        <form data-testid='clients-form' onSubmit={handleSubmit}>
          <label>
            Client ID:
            <input type="text" name="clientId" value={formState.clientId} onChange={handleFormChange} required/>
            {fieldErrors['clientId'] &&
              <span data-testid='validationError' className="error">{fieldErrors['clientId']}</span>}<br/>
          </label>
          <label>
            Description:
            <input type="text" name="description" value={formState.description} onChange={handleFormChange}/>
          </label>
          <label>
            Owner:
            <input type="text" name="owner" value={formState.owner} onChange={handleFormChange} required/>
            <span data-testid='validationError' className="error">{fieldErrors['owner']}</span><br/>
          </label>
          <div className="grid-item one-whole">
            <div className="checkbox-container">
              <input className="input-checkbox" type="checkbox" name="publicClient" id="publicClient"
                     onChange={handleFormChange} value={formState.publicClient.toString()}/>
              <label htmlFor="publicClient" className="label-checkbox">Is public client? true if your client is out of
                our control once is delivered. For example a mobile app</label>
            </div>
            <div className="checkbox-container">
              <input className="input-checkbox" type="checkbox" name="thirdPartyClient" id="thirdPartyClient"
                     onChange={handleFormChange} value={formState.thirdPartyClient.toString()}/>
              <label htmlFor="thirdPartyClient" className="label-checkbox">Is 3rd party client? true if your client will
                be used by third party companies to access our public apis</label>
            </div>
          </div>
          <label>
            Scopes:
            <input type="text" name="scopes" value={formState.scopes} onChange={handleFormChange} required/>
          </label>
          <fieldset>
            <ul>
              <li className="radio-container">
                <input type="radio" className="input-radio" id="clientCredentials" name="grantTypes"
                       checked={formState.grantTypes === GrantTypes.CLIENT_CREDENTIALS} onChange={handleFormChange}
                       value={GrantTypes.CLIENT_CREDENTIALS}/>
                <label htmlFor="clientCredentials" className="label-radio">Client Credentials</label>
              </li>
              <li className="radio-container">
                <input type="radio" className="input-radio" id="authorizationCode" name="grantTypes"
                       checked={formState.grantTypes === GrantTypes.AUTHORIZATION_CODE} onChange={handleFormChange}
                       value={GrantTypes.AUTHORIZATION_CODE}/>
                <label htmlFor="authorizationCode" className="label-radio">Authorization Code</label>
              </li>
            </ul>
          </fieldset>
          <label>
            Redirect Uris:
            <input type="text" name="redirectUris" value={formState.redirectUris} onChange={handleFormChange}/>
          </label>
          {loading ? <Modal onClose={() => setShowModal(false)}><h3>Creating Client...</h3></Modal> :
            <button className="button-primary" type="submit">Create Client</button>}
        </form>
      )}
      {clientCredentials && (
        <ClientCredentialsContent clientCredentials={clientCredentials}/>
      )}
    </div>
  );
};

const CreateClientsPage: React.FC = () => {
  return <ClientCreationContent/>;
};

export default CreateClientsPage;
